import { datadogRum } from '@datadog/browser-rum'
import { getArkosePublicKey } from '@/helpers/env'
import logger from '@/helpers/logger'

export enum ArkoseAction {
  CREATE_RESERVATION = 'CREATE_RESERVATION',
  MODIFY_RESERVATION = 'MODIFY_RESERVATION',
  CANCEL_RESERVATION = 'CANCEL_RESERVATION',
  WAITLIST_JOIN = 'WAITLIST_JOIN',
}

// Countable client-side signal for challenge outcomes that never reach
// host-legacy (dismissal, non-render, script failure) and would otherwise
// be invisible on both sides of the integration.
const trackArkoseEvent = (
  outcome:
    | 'shown'
    | 'completed'
    | 'failed'
    | 'abandoned'
    | 'error'
    | 'script_load_failed'
    | 'script_load_timeout'
    | 'script_load_cooldown'
    | 'challenge_timeout',
  action: ArkoseAction
) => {
  datadogRum.addAction('arkose_challenge', { outcome, action })
}

const ARKOSE_SCRIPT_BASE_URL = 'https://olo-api.arkoselabs.com/v2'
const SCRIPT_LOAD_TIMEOUT_MS = 10000
// Long enough for a guest to solve a visible puzzle (a slow solve on a phone
// shouldn't send the request tokenless). Challenges are serialized through a
// single queue (see challengeQueueTail below), so a stuck one blocks anything
// queued behind it for this long, which is rare now that only the mutating
// calls run challenges.
const CHALLENGE_TIMEOUT_MS = 30000
const SCRIPT_LOAD_RETRY_COOLDOWN_MS = 30000

interface ArkoseEnforcementClient {
  setConfig: (config: {
    onCompleted: (response: { token: string }) => void
    onReady: () => void
    onFailed: () => void
    onShown: () => void
    onError: (response: unknown) => void
    onHide: () => void
  }) => void
  run: () => void
  reset: () => void
}

declare global {
  interface Window {
    arkose_setupEnforcement?: (client: ArkoseEnforcementClient) => void
  }
}

let enforcementClientPromise: Promise<ArkoseEnforcementClient> | undefined
let inScriptLoadCooldown = false

// Only one setConfig/run() can be active on the shared enforcement client
// at a time (Arkose only supports one client per page), so challenges are
// serialized through this queue rather than run concurrently.
let challengeQueueTail: Promise<unknown> = Promise.resolve()

export const isEnabled = () => {
  const key = getArkosePublicKey()
  return !!key
}

const loadScript = (): Promise<ArkoseEnforcementClient> => {
  if (!enforcementClientPromise) {
    if (inScriptLoadCooldown) {
      return Promise.reject(new Error('Arkose script load in cooldown'))
    }

    enforcementClientPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Arkose script load timed out'))
      }, SCRIPT_LOAD_TIMEOUT_MS)

      window.arkose_setupEnforcement = client => {
        clearTimeout(timeout)
        resolve(client)
      }

      const script = document.createElement('script')
      script.src = `${ARKOSE_SCRIPT_BASE_URL}/${getArkosePublicKey()}/api.js`
      script.setAttribute('data-callback', 'arkose_setupEnforcement')
      script.async = true
      script.onerror = () => {
        clearTimeout(timeout)
        reject(new Error('Arkose script failed to load'))
      }

      document.head.appendChild(script)
    })

    // Let a later challenge retry the script load instead of being stuck
    // with a permanently-rejected memoized promise. Retrying immediately
    // on every subsequent runChallenge call would mean a guest who can
    // never load Arkose (network policy, CSP, ad blocker, ...) eats a
    // fresh 10s load timeout on every action, so hold off retrying again
    // until this cooldown passes.
    enforcementClientPromise.catch(() => {
      enforcementClientPromise = undefined
      inScriptLoadCooldown = true
      setTimeout(() => {
        inScriptLoadCooldown = false
      }, SCRIPT_LOAD_RETRY_COOLDOWN_MS)
    })
  }

  return enforcementClientPromise
}

const runChallengeUncached = async (action: ArkoseAction): Promise<string> => {
  try {
    const client = await loadScript()

    return await new Promise<string>(resolve => {
      let settled = false
      const settleOnce = (token: string) => {
        if (settled) {
          return
        }
        settled = true
        clearTimeout(timeout)
        resolve(token)
      }

      const timeout = setTimeout(() => {
        logger({ message: `Arkose challenge timed out for action ${action}` })
        trackArkoseEvent('challenge_timeout', action)
        // Settle before attempting reset() — if reset() throws, the
        // promise must still resolve, or the queue behind it deadlocks.
        settleOnce('')
        // The lightbox is still open at this point with nothing left
        // listening for a result — close it rather than leave the guest
        // staring at a live challenge for a request that already went out.
        try {
          client.reset()
        } catch (error) {
          logger({ error })
        }
      }, CHALLENGE_TIMEOUT_MS)

      client.setConfig({
        onReady: () => undefined,
        onCompleted: response => {
          trackArkoseEvent('completed', action)
          settleOnce(response.token)
        },
        onFailed: () => {
          logger({ message: `Arkose challenge failed for action ${action}` })
          trackArkoseEvent('failed', action)
          settleOnce('')
        },
        onError: response => {
          logger({ error: response })
          trackArkoseEvent('error', action)
          settleOnce('')
        },
        onShown: () => trackArkoseEvent('shown', action),
        // Guest dismissed/closed the challenge without completing it.
        // Resolve immediately (no token) rather than waiting on the
        // challenge timeout — the request goes out tokenless right away and the
        // backend's 401 (if any) drives the existing error+retry UI.
        // Arkose also fires onHide after a completed/failed/errored
        // challenge closes, so only count it as abandoned if nothing
        // has settled yet.
        onHide: () => {
          if (!settled) {
            trackArkoseEvent('abandoned', action)
          }
          settleOnce('')
        },
      })

      client.run()
    })
  } catch (error) {
    const isCooldown =
      error instanceof Error && error.message.includes('cooldown')
    const isScriptLoadTimeout =
      error instanceof Error && error.message.includes('timed out')

    if (isCooldown) {
      trackArkoseEvent('script_load_cooldown', action)
      return ''
    }

    trackArkoseEvent(
      isScriptLoadTimeout ? 'script_load_timeout' : 'script_load_failed',
      action
    )
    logger({ error })
    return ''
  }
}

export const runChallenge = (action: ArkoseAction): Promise<string> => {
  if (!isEnabled()) {
    return Promise.resolve('')
  }

  // Arkose tokens are single-use: every caller gets its own challenge (and
  // token) rather than sharing an in-flight one, or the second request to
  // verify the shared token is rejected as previously verified.
  return new Promise<string>(resolve => {
    challengeQueueTail = challengeQueueTail
      .then(() => runChallengeUncached(action))
      // runChallengeUncached never rejects today, but if that ever
      // changes, resolve this challenge with '' (fail open, consistent
      // with every other failure path) instead of leaving it stuck —
      // and swallow the rejection here so it can't propagate and
      // permanently poison the shared queue tail for every later action.
      .catch(() => '')
      .then(resolve)
  })
}

/**
 * A present-but-empty arkose-token header can be rejected as malformed by
 * some server/proxy configs, so omit the header entirely when there's no
 * token rather than sending ''.
 */
export const arkoseTokenHeader = (token: string) =>
  token ? { 'arkose-token': token } : {}

export const isArkoseRejection = (error: unknown): boolean => {
  const status = (error as any)?.response?.status
  return status === 401
}

/**
 * axios runs transformResponse on error bodies too, and a transform that
 * assumes a success shape (e.g. `JSON.parse(data).types.map(...)`) will
 * throw on an error body like `{"message": "..."}`. That thrown error
 * replaces axios's own rejection, losing `error.response.status` and
 * making a 401 undetectable by isArkoseRejection. Wrap a transform so a
 * failure on an error body falls through to axios's real rejection
 * instead of masking it.
 */
export const safeTransformResponse =
  (transform: (data: any, headers?: any) => any) =>
  (data: any, headers?: any) => {
    try {
      return transform(data, headers)
    } catch {
      return data
    }
  }

export const ARKOSE_REJECTED_MESSAGE =
  "We couldn't verify your request. Please try again."
